import React, { useRef, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { isEmpty } from "lodash";
import { descripcionGrafico, paletaGraficos, pluginsGrafico } from "../../../theme/graficos";
import { useDatoPanel } from "../useDatoPanel";
import { alturaDona, ordenarDeMayorAMenor, sumar, textoPorcentaje } from "../panel";
import SeccionPanel from "../SeccionPanel";

ChartJS.register(ArcElement, Tooltip, Legend);

const FORMATO_ENTERO = new Intl.NumberFormat("es-NI", { maximumFractionDigits: 0 });

// Lado de la dona; la tabla ocupa el resto del ancho.
const LADO_DONA = 220;

// Como la Colocación semanal: borde en el color vivo de la serie y relleno tenue del mismo color.
const RELLENO = 0.28;
const RELLENO_SENALADO = 0.5;

/**
 * Reparto de un conteo por grupo: dona a la izquierda y, a la derecha, una tabla con el color,
 * el nombre completo, la cantidad y el porcentaje de cada grupo, de mayor a menor (en el teléfono
 * la tabla va debajo). El centro de la dona dice el grupo señalado (al pasar el mouse por una
 * porción o por una fila) o, si no hay ninguno, el más grande.
 *
 * Sin total: los servicios cuentan con otro criterio que los indicadores (p. ej. créditos
 * entregados sin cancelar, también los eliminados), y el total no coincidiría con ellos.
 *
 * @param {object} props
 * @param {string} props.titulo
 * @param {Function} props.obtener Función de `api/Dashboard`; responde `[{ ..., contador }]`.
 * @param {(item: object) => string} props.nombre Nombre del grupo.
 * @param {string} props.columna Encabezado de la columna del nombre ("Municipio").
 * @param {[string, string]} props.unidad Singular y plural ("cliente", "clientes").
 * @param {string} props.descripcionVacio
 */
const DonaPorGrupo = ({ titulo, obtener, nombre, columna, unidad, descripcionVacio }) => {
  const [datos, setDatos] = useState([]);
  const [activo, setActivo] = useState(null);
  const grafico = useRef(null);
  const paleta = paletaGraficos(useTheme());

  const { cargando, error, recargar } = useDatoPanel(obtener, (respuesta) =>
    setDatos(ordenarDeMayorAMenor(respuesta.result, (i) => i.contador))
  );

  const total = sumar(datos.map((i) => i.contador));
  const colores = paleta.colores(datos.length);
  const conUnidad = (n) => `${FORMATO_ENTERO.format(n)} ${n === 1 ? unidad[0] : unidad[1]}`;
  const enfocado = datos[activo ?? 0];

  // Señalar una fila resalta su porción, y al revés.
  const senalar = (indice) => {
    setActivo(indice);
    const chart = grafico.current;
    if (!chart) return;
    chart.setActiveElements(indice === null ? [] : [{ datasetIndex: 0, index: indice }]);
    chart.update();
  };

  const data = {
    labels: datos.map((i) => nombre(i)),
    datasets: [
      {
        data: datos.map((i) => i.contador),
        backgroundColor: colores.map((c) => alpha(c, RELLENO)),
        hoverBackgroundColor: colores.map((c) => alpha(c, RELLENO_SENALADO)),
        // El borde vivo (3:1 o más sobre la tarjeta) marca cada porción; el espacio entre ellas
        // las separa sin depender del color.
        borderColor: colores,
        hoverBorderColor: colores,
        borderWidth: 2,
        spacing: 2,
        hoverOffset: 8,
      },
    ],
  };

  const base = pluginsGrafico(paleta);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "64%",
    layout: { padding: 8 },
    plugins: { ...base, legend: { display: false }, tooltip: { enabled: false } },
    onHover: (evento, elementos) => setActivo(elementos.length ? elementos[0].index : null),
  };

  return (
    <SeccionPanel
      titulo={titulo}
      cargando={cargando}
      error={error}
      vacio={isEmpty(datos)}
      descripcionVacio={descripcionVacio}
      onReintentar={recargar}
      altura={alturaDona(datos.length, LADO_DONA)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2, sm: 4 },
          height: "100%",
        }}
      >
        <Box
          sx={{ position: "relative", width: LADO_DONA, height: LADO_DONA, flexShrink: 0 }}
          onMouseLeave={() => senalar(null)}
        >
          <Doughnut
            ref={grafico}
            data={data}
            options={options}
            aria-label={descripcionGrafico(titulo, data, conUnidad)}
          />
          {enfocado && (
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography
                variant="monto"
                component="span"
                sx={{ fontSize: "1.75rem", lineHeight: 1.1, color: "text.primary" }}
              >
                {textoPorcentaje(enfocado.contador, total)}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 700, color: "text.primary", maxWidth: 120, lineHeight: 1.2 }}
              >
                {nombre(enfocado)}
              </Typography>
              <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                {conUnidad(enfocado.contador)}
              </Typography>
            </Box>
          )}
        </Box>

        <Table
          size="small"
          aria-label={titulo}
          sx={{
            width: "100%",
            maxWidth: { sm: 520 },
            "& th, & td": { borderBottom: 0, py: 0, px: 1, height: 30 },
            "& th": { color: "text.secondary", fontWeight: 700 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{columna}</TableCell>
              <TableCell align="right">{unidad[1].charAt(0).toUpperCase() + unidad[1].slice(1)}</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datos.map((item, i) => (
              <TableRow
                key={`${nombre(item)}-${i}`}
                hover
                selected={activo === i}
                onMouseEnter={() => senalar(i)}
                onMouseLeave={() => senalar(null)}
              >
                <TableCell sx={{ maxWidth: 0, width: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "3px",
                        flexShrink: 0,
                        bgcolor: alpha(colores[i], RELLENO),
                        border: `2px solid ${colores[i]}`,
                      }}
                    />
                    <Box
                      component="span"
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={nombre(item)}
                    >
                      {nombre(item)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {FORMATO_ENTERO.format(item.contador)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {textoPorcentaje(item.contador, total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SeccionPanel>
  );
};

export default DonaPorGrupo;
